/**
 * HistorySheet — modal listing past Founsi threads, grouped into
 * Today / Yesterday / This Week / Earlier buckets.  Tap-to-resume.
 *
 * PR1 of #261 ships read + resume.  Rename / delete / pin /
 * long-press menu live in PR2; search + infinite scroll in PR3.
 *
 * Implementation note: we render as a `Modal` (same pattern as
 * CommandPalette) rather than wiring `react-native-drawer-layout`.
 * Modal keeps the dep surface flat and the interaction (slide-in
 * sheet from the right with a scrim) is good enough on web —
 * native-side drawer ergonomics will get re-evaluated when the
 * native build ships.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  getThreadMessages,
  ThreadsHttpError,
  type ThreadSummary,
} from '../api/threadsApi';
import {
  deleteThread,
  loadMoreThreads,
  loadThreads,
  renameThread,
  setSearchQuery,
  setThreadPinned,
} from '../founsiHistorySlice';
import { replayThread, startNewThread } from '../founsiSlice';
import { useDateBuckets, type DateBucket } from '../hooks/useDateBuckets';

interface Props {
  visible: boolean;
  onClose: () => void;
}

// ─── Row ────────────────────────────────────────────────────────────

interface ThreadRowProps {
  thread: ThreadSummary;
  /** Id of the row whose menu popover is currently open (sheet-scoped). */
  menuOpenId: string | null;
  /** Id of the row currently in inline-rename mode. */
  renamingId: string | null;
  onPick: () => void;
  onToggleMenu: () => void;
  onStartRename: () => void;
  onCommitRename: (newTitle: string) => void;
  onCancelRename: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
}

const ThreadListItem: React.FC<ThreadRowProps> = ({
  thread,
  menuOpenId,
  renamingId,
  onPick,
  onToggleMenu,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onTogglePin,
  onDelete,
}) => {
  const isMenuOpen = menuOpenId === thread.id;
  const isRenaming = renamingId === thread.id;
  const [draft, setDraft] = useState(thread.title);
  // Keep the draft in sync if the thread title is updated externally
  // (e.g. another mutation) while the row is NOT in rename mode.
  useEffect(() => {
    if (!isRenaming) setDraft(thread.title);
  }, [thread.title, isRenaming]);

  const ts = formatTimestamp(thread.last_message_at);
  return (
    <View style={styles.rowOuter}>
      <Pressable
        onPress={isRenaming ? undefined : onPick}
        style={({ pressed }) => [
          styles.row,
          pressed && !isRenaming && styles.rowPressed,
        ]}
        accessibilityRole="button"
        accessibilityLabel={`Resume thread: ${thread.title}`}
        testID={`founsi-history-row-${thread.id}`}
      >
        <View style={styles.rowMain}>
          {isRenaming ? (
            <TextInput
              value={draft}
              onChangeText={setDraft}
              autoFocus
              onSubmitEditing={() => onCommitRename(draft)}
              onBlur={() => onCommitRename(draft)}
              style={styles.renameInput}
              placeholder="Thread title"
              testID={`founsi-history-rename-input-${thread.id}`}
              maxLength={80}
            />
          ) : (
            <Text style={styles.rowTitle} numberOfLines={1}>
              {thread.pinned ? (
                <MaterialIcons
                  name="push-pin"
                  size={12}
                  color={fpColors.onSurfaceVariant}
                />
              ) : null}
              {thread.pinned ? ' ' : ''}
              {thread.title || 'Untitled thread'}
            </Text>
          )}
          {isRenaming ? null : (
            <Text style={styles.rowPreview} numberOfLines={1}>
              {thread.preview || '…'}
            </Text>
          )}
        </View>
        {isRenaming ? (
          <Pressable
            onPress={onCancelRename}
            style={styles.kebabBtn}
            accessibilityLabel="Cancel rename"
            testID={`founsi-history-rename-cancel-${thread.id}`}
          >
            <MaterialIcons name="close" size={16} color={fpColors.onSurfaceVariant} />
          </Pressable>
        ) : (
          <>
            <Text style={styles.rowTime}>{ts}</Text>
            <Pressable
              onPress={onToggleMenu}
              style={styles.kebabBtn}
              accessibilityRole="button"
              accessibilityLabel="More actions"
              testID={`founsi-history-menu-btn-${thread.id}`}
            >
              <MaterialIcons
                name="more-vert"
                size={16}
                color={fpColors.onSurfaceVariant}
              />
            </Pressable>
          </>
        )}
      </Pressable>

      {isMenuOpen ? (
        <View
          style={styles.menuPopover}
          testID={`founsi-history-menu-${thread.id}`}
        >
          <Pressable
            onPress={onStartRename}
            style={({ pressed }) => [
              styles.menuItem,
              pressed && styles.menuItemPressed,
            ]}
            testID={`founsi-history-menu-rename-${thread.id}`}
          >
            <MaterialIcons name="edit" size={14} color={fpColors.onSurface} />
            <Text style={styles.menuItemText}>Rename</Text>
          </Pressable>
          <Pressable
            onPress={onTogglePin}
            style={({ pressed }) => [
              styles.menuItem,
              pressed && styles.menuItemPressed,
            ]}
            testID={`founsi-history-menu-pin-${thread.id}`}
          >
            <MaterialIcons
              name={thread.pinned ? 'push-pin' : 'push-pin'}
              size={14}
              color={fpColors.onSurface}
            />
            <Text style={styles.menuItemText}>
              {thread.pinned ? 'Unpin' : 'Pin to top'}
            </Text>
          </Pressable>
          <Pressable
            onPress={onDelete}
            style={({ pressed }) => [
              styles.menuItem,
              pressed && styles.menuItemPressed,
            ]}
            testID={`founsi-history-menu-delete-${thread.id}`}
          >
            <MaterialIcons name="delete" size={14} color={fpColors.error ?? '#B00020'} />
            <Text
              style={[styles.menuItemText, { color: fpColors.error ?? '#B00020' }]}
            >
              Delete
            </Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
};

/** Compact time label: "10:42" for today, "Mon" for this week,
 *  "May 28" for older.  Mirrors the spec wireframe. */
function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  const ms = +now - +d;
  if (ms < 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ─── Sheet ──────────────────────────────────────────────────────────

export const HistorySheet: React.FC<Props> = ({ visible, onClose }) => {
  const dispatch = useAppDispatch();
  const { threads, loading, error, hasLoadedOnce, hasMore, loadingMore, searchQuery } =
    useAppSelector((s) => s.founsiHistory);
  const currentThreadId = useAppSelector((s) => s.founsi.threadId);
  const buckets = useDateBuckets(threads);

  // Per-row menu state — at most one menu open at a time.
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  // Local mirror of searchQuery so the input stays responsive while
  // the debounced fetch settles.  Kept in sync with the slice on
  // sheet open + on slice changes (e.g. an external clear).
  const [searchDraft, setSearchDraft] = useState(searchQuery);

  // Load on first open + on every re-open (cheap — the user expects
  // a fresh list when they re-open the drawer).
  useEffect(() => {
    if (visible) {
      dispatch(loadThreads());
      // Close any stale menu / rename state from a prior open.
      setMenuOpenId(null);
      setRenamingId(null);
      setSearchDraft(searchQuery);
    }
    // intentionally omit searchQuery from deps — only re-sync on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, dispatch]);

  // Debounced search: 300ms after the last keystroke, push the draft
  // into the slice and re-fetch.  Matches the `inflightRef` pattern
  // used elsewhere (BrowseScreen) but simpler — the loadThreads
  // thunk drops stale results internally via the `q` check.
  useEffect(() => {
    if (!visible) return;
    if (searchDraft === searchQuery) return;
    const t = setTimeout(() => {
      dispatch(setSearchQuery(searchDraft));
      dispatch(loadThreads());
    }, 300);
    return () => clearTimeout(t);
  }, [searchDraft, searchQuery, visible, dispatch]);

  const handleRename = useCallback(
    (thread: ThreadSummary, newTitle: string) => {
      const trimmed = newTitle.trim();
      setRenamingId(null);
      setMenuOpenId(null);
      // No-op if unchanged or empty
      if (!trimmed || trimmed === thread.title) return;
      dispatch(renameThread({ id: thread.id, title: trimmed }));
    },
    [dispatch],
  );

  const handleTogglePin = useCallback(
    (thread: ThreadSummary) => {
      setMenuOpenId(null);
      dispatch(setThreadPinned({ id: thread.id, pinned: !thread.pinned }));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (thread: ThreadSummary) => {
      setMenuOpenId(null);
      // CLAUDE.md: `Alert.alert` is a no-op on web — use window.confirm.
      const confirmed =
        typeof window !== 'undefined' && window.confirm
          ? window.confirm(
              `Delete "${thread.title || 'this thread'}"? This can't be undone.`,
            )
          : true;
      if (!confirmed) return;
      // If the user deletes the thread they currently have open in
      // the chat surface, clear that surface too so they don't see a
      // half-orphaned conversation referring to nothing on the BE.
      if (currentThreadId === thread.id) {
        dispatch(startNewThread());
      }
      dispatch(deleteThread({ id: thread.id }));
    },
    [dispatch, currentThreadId],
  );

  const handlePick = useCallback(
    async (thread: ThreadSummary) => {
      try {
        const res = await getThreadMessages(thread.id);
        dispatch(
          replayThread({
            threadId: thread.id,
            title: thread.title,
            messages: res.messages,
          }),
        );
        onClose();
      } catch (err) {
        // Cross-tenant returns 404 with `code: founsi_thread_not_found`
        // — surface a non-leaky message either way (per the audit
        // on #261, never differentiate "yours but deleted" from
        // "not yours" in copy).
        const msg =
          err instanceof ThreadsHttpError
            ? `Couldn't open this thread: ${err.message}`
            : "Couldn't open this thread.";
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(msg);
        }
      }
    },
    [dispatch, onClose],
  );

  // Build a single flat list of {section} | {thread} rows for the
  // FlatList renderer (RN's SectionList works on web but the
  // sticky-header behavior is inconsistent — a flat list keeps
  // styling predictable).
  type Row =
    | { type: 'section'; key: string; label: string }
    | { type: 'thread'; key: string; thread: ThreadSummary };
  const rows: Row[] = [];
  for (const b of buckets) {
    rows.push({ type: 'section', key: `sec-${b.key}`, label: b.label });
    for (const t of b.threads) {
      rows.push({ type: 'thread', key: t.id, thread: t });
    }
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        style={styles.scrim}
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel="Close history"
      >
        <Pressable
          style={styles.sheet}
          onPress={(e) => e.stopPropagation()}
          testID="founsi-history-sheet"
        >
          <View style={styles.header}>
            <MaterialIcons name="history" size={18} color={fpColors.onSurface} />
            <Text style={styles.headerTitle}>History</Text>
            <Pressable
              onPress={onClose}
              style={styles.closeBtn}
              accessibilityRole="button"
              accessibilityLabel="Close"
              testID="founsi-history-close"
            >
              <MaterialIcons name="close" size={18} color={fpColors.onSurface} />
            </Pressable>
          </View>

          {/* Search bar — debounced 300ms, server-side fuzzy via `?q=`. */}
          <View style={styles.searchRow}>
            <MaterialIcons name="search" size={16} color={fpColors.outline} />
            <TextInput
              value={searchDraft}
              onChangeText={setSearchDraft}
              placeholder="Search history…"
              placeholderTextColor={fpColors.outline}
              style={styles.searchInput}
              autoCapitalize="none"
              autoCorrect={false}
              testID="founsi-history-search-input"
              returnKeyType="search"
            />
            {searchDraft ? (
              <Pressable
                onPress={() => setSearchDraft('')}
                style={styles.searchClearBtn}
                accessibilityLabel="Clear search"
                testID="founsi-history-search-clear"
              >
                <MaterialIcons
                  name="close"
                  size={14}
                  color={fpColors.onSurfaceVariant}
                />
              </Pressable>
            ) : null}
          </View>

          {loading && !hasLoadedOnce ? (
            <View style={styles.center}>
              <ActivityIndicator color={fpColors.primary} />
            </View>
          ) : error && threads.length === 0 ? (
            <View style={styles.center}>
              <Text style={styles.errorText}>{error}</Text>
              <Pressable
                onPress={() => dispatch(loadThreads())}
                style={styles.retryBtn}
                testID="founsi-history-retry"
              >
                <Text style={styles.retryBtnText}>Try again</Text>
              </Pressable>
            </View>
          ) : rows.length === 0 ? (
            <View style={styles.center}>
              <Text style={styles.emptyText}>
                {searchQuery
                  ? `No threads match "${searchQuery}".`
                  : 'Past conversations will appear here.'}
              </Text>
            </View>
          ) : (
            <FlatList
              data={rows}
              keyExtractor={(r) => r.key}
              renderItem={({ item }) =>
                item.type === 'section' ? (
                  <Text style={styles.sectionHeader}>{item.label}</Text>
                ) : (
                  <ThreadListItem
                    thread={item.thread}
                    menuOpenId={menuOpenId}
                    renamingId={renamingId}
                    onPick={() => handlePick(item.thread)}
                    onToggleMenu={() =>
                      setMenuOpenId((id) =>
                        id === item.thread.id ? null : item.thread.id,
                      )
                    }
                    onStartRename={() => {
                      setMenuOpenId(null);
                      setRenamingId(item.thread.id);
                    }}
                    onCommitRename={(t) => handleRename(item.thread, t)}
                    onCancelRename={() => setRenamingId(null)}
                    onTogglePin={() => handleTogglePin(item.thread)}
                    onDelete={() => handleDelete(item.thread)}
                  />
                )
              }
              keyboardShouldPersistTaps="handled"
              testID="founsi-history-list"
              onEndReached={() => {
                if (hasMore && !loadingMore) dispatch(loadMoreThreads());
              }}
              onEndReachedThreshold={0.4}
              ListFooterComponent={
                loadingMore ? (
                  <View style={styles.footerLoader}>
                    <ActivityIndicator color={fpColors.primary} size="small" />
                  </View>
                ) : !hasMore && threads.length > 0 ? (
                  <Text style={styles.footerEnd}>End of history</Text>
                ) : null
              }
            />
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    // Mobile note: bumped from 0.55 → 0.68 so the composer button +
    // bottom tabs don't bleed through the right-side scrim strip on
    // narrow viewports (verified against iPhone 13 / 375px).
    backgroundColor: 'rgba(31, 26, 34, 0.68)',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  sheet: {
    width: Platform.OS === 'web' ? 360 : '85%',
    maxWidth: '85%',
    height: '100%',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRightWidth: 1,
    borderRightColor: fpColors.outlineVariant,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 4, height: 0 },
    shadowRadius: 16,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 14,
    paddingBottom: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
    gap: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  closeBtn: {
    // Mobile: bumped from 32×32 → 44×44 to meet iOS HIG touch target.
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: -8, // optical: align icon with row content edge
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 40,
  },
  errorText: {
    fontSize: 13,
    color: fpColors.error ?? '#B00020',
    textAlign: 'center',
    marginBottom: 12,
  },
  retryBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  retryBtnText: {
    fontSize: 13,
    color: fpColors.primary,
    fontWeight: '600',
  },
  emptyText: {
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    paddingVertical: 4,
    padding: 0,
    margin: 0,
    minHeight: 24,
  },
  searchClearBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  footerLoader: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  footerEnd: {
    paddingVertical: 16,
    textAlign: 'center',
    fontSize: 11,
    color: fpColors.outline,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: '700',
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.6,
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 6,
    textTransform: 'uppercase',
  },
  rowOuter: {
    // Wrap the Pressable so the menu popover can position itself
    // relative to the row without breaking the FlatList layout.
    position: 'relative',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 8,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  rowMain: {
    flex: 1,
  },
  renameInput: {
    fontSize: 14,
    fontWeight: '600',
    color: fpColors.onSurface,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: fpColors.primary,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  kebabBtn: {
    // Mobile: bumped to 40×40 hit area (icon stays 16px) so the
    // per-row menu is comfortably tappable with a thumb without
    // mis-firing the row's resume Pressable underneath.  The
    // negative margins keep the visual layout unchanged.
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
    marginTop: -10,
    marginRight: -8,
    marginBottom: -10,
  },
  menuPopover: {
    position: 'absolute',
    top: 36,
    right: 8,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowOffset: { width: 0, height: 6 },
    shadowRadius: 14,
    elevation: 6,
    zIndex: 10,
    minWidth: 140,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    // Mobile: bumped from 8 → 12 so each menu row is ~44px tall —
    // hits iOS HIG touch-target minimum without an icon-size bump.
    paddingVertical: 12,
  },
  menuItemPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  menuItemText: {
    fontSize: 13,
    color: fpColors.onSurface,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  rowPreview: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  rowTime: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
});

export default HistorySheet;
